import React, { useEffect, useRef, useState } from "react";
import ConfirmationModal from "@enerex/unified-ui-react/ConfirmationModal";
import Check from "@enerex/unified-ui-react/form/Check";
import FormInput from "@enerex/unified-ui-react/form/FormInput";
import Select from "@enerex/unified-ui-react/form/Select";
import { useForm, useWatch } from "react-hook-form";
import * as api from "../utilityApi";
import { CommodityIcon } from "../CommodityIcons";
import { getUtilityStateCommodityIds } from "./utilityStateRows";
import "./EditStateOperationModal.css";

export const getSopEditSnapshot = (row) => {
  const mappings = row?.commodityMappings ?? row?.CommodityMappings;
  if (!Array.isArray(mappings)) {
    const id = row?.id ?? row?.Id;
    const commodityId = Number(row?.commodityId ?? row?.CommodityId);
    if (!/^\d+$/.test(String(id)) || (typeof id === "number" && !Number.isSafeInteger(id)) ||
        (commodityId !== 1 && commodityId !== 2)) return null;
    return { expectedMappingIds: [String(id)], commodityIds: [commodityId] };
  }
  if (mappings.length !== 1) return null;
  const ids = mappings.map((mapping) => mapping.id ?? mapping.Id);
  const commodities = mappings.map((mapping) => Number(mapping.commodityId ?? mapping.CommodityId));
  if (ids.some((id) => !/^\d+$/.test(String(id)) || (typeof id === "number" && !Number.isSafeInteger(id))) ||
      commodities.some((id) => id !== 1 && id !== 2) ||
      new Set(ids.map(String)).size !== ids.length || new Set(commodities).size !== commodities.length) return null;
  return { expectedMappingIds: ids.map(String), commodityIds: commodities };
};

const EditStateOperationModal = ({ row, stateLabel, saving, onHide, onSave }) => {
  const [error, setError] = useState("");
  const [uomOptions, setUomOptions] = useState([]);
  const { control, getValues, reset, setValue } = useForm({
    defaultValues: {
      ServiceReferenceNumberRequired: false,
      ServiceReferenceNumberDescription: "",
      KeyNameRequired: false,
      KeyNameDescription: "",
      EnrollmentLeadTime: "",
      EnerexResidentialUOM: null,
      EnerexCommercialUOM: null,
      EnerexLargeCommercialUOM: null,
      CommodityElectric: false,
      CommodityGas: false,
    },
  });
  const nameKeyRequired = useWatch({ control, name: "KeyNameRequired" });
  const serviceReferenceRequired = useWatch({ control, name: "ServiceReferenceNumberRequired" });
  const commodityElectric = useWatch({ control, name: "CommodityElectric" });
  const commodityGas = useWatch({ control, name: "CommodityGas" });
  const previousCommodityRef = useRef({ electric: commodityElectric, gas: commodityGas });
  useEffect(() => {
    if (commodityElectric && commodityGas) {
      if (!previousCommodityRef.current.electric && commodityElectric) setValue("CommodityGas", false);
      else setValue("CommodityElectric", false);
    }
    previousCommodityRef.current = { electric: commodityElectric, gas: commodityGas };
  }, [commodityElectric, commodityGas, setValue]);

  useEffect(() => {
    if (!row) return;
    let active = true;
    api.getUnitMeasurements().then((records) => {
      if (active) setUomOptions(records.map((record) => ({ id: record.id ?? record.Id, name: record.name ?? record.Name })));
    }).catch(() => { if (active) setUomOptions([]); });
    return () => { active = false; };
  }, [row]);

  useEffect(() => {
    const snapshot = getSopEditSnapshot(row);
    setError(row && !snapshot ? "Mapping details are unavailable. Refresh the page before editing." : "");
    reset({
      ServiceReferenceNumberRequired: Boolean(row?.serviceReferenceNumberRequired ?? row?.ServiceReferenceNumberRequired),
      ServiceReferenceNumberDescription: row?.serviceReferenceNumberDescription ?? row?.ServiceReferenceNumberDescription ?? "",
      KeyNameRequired: Boolean(row?.keyNameRequired ?? row?.nameKeyRequired ?? row?.KeyNameRequired ?? row?.NameKeyRequired),
      KeyNameDescription: row?.keyNameDescription ?? row?.nameKeyDescription ?? row?.KeyNameDescription ?? row?.NameKeyDescription ?? "",
      EnrollmentLeadTime: row?.enrollmentLeadTime ?? row?.EnrollmentLeadTime ?? "",
      EnerexResidentialUOM: uomOptions.find((uom) => String(uom.id).toLowerCase() === String(row?.enerexResidentialUOM ?? row?.EnerexResidentialUOM ?? "").toLowerCase()) || null,
      EnerexCommercialUOM: uomOptions.find((uom) => String(uom.id).toLowerCase() === String(row?.enerexCommercialUOM ?? row?.EnerexCommercialUOM ?? "").toLowerCase()) || null,
      EnerexLargeCommercialUOM: uomOptions.find((uom) => String(uom.id).toLowerCase() === String(row?.enerexLargeCommercialUOM ?? row?.EnerexLargeCommercialUOM ?? "").toLowerCase()) || null,
      CommodityElectric: getUtilityStateCommodityIds(row).includes(1),
      CommodityGas: getUtilityStateCommodityIds(row).includes(2),
    });
  }, [reset, row, uomOptions]);

  return (
    <ConfirmationModal
      show={Boolean(row)}
      size="md"
      centered
      title="Edit State of Operations"
      confirmButtonLabel={saving ? "Saving..." : "Save"}
      isLoading={saving}
      keyboard={!saving}
      onHide={() => { if (!saving) onHide(); }}
      onConfirm={() => {
        if (saving || !getSopEditSnapshot(row)) return;
        const values = getValues();
        const commodityIds = [values.CommodityElectric && 1, values.CommodityGas && 2].filter(Boolean);
        if (!commodityIds.length) return;
        setError("");
        onSave({
          commodityIds,
          serviceReferenceNumberRequired: Boolean(values.ServiceReferenceNumberRequired),
          serviceReferenceNumberDescription: values.ServiceReferenceNumberDescription || null,
          keyNameRequired: Boolean(values.KeyNameRequired),
          keyNameDescription: values.KeyNameDescription ?? "",
          enrollmentLeadTime: values.EnrollmentLeadTime ?? "",
          enerexResidentialUOM: values.EnerexResidentialUOM?.id ?? null,
          enerexCommercialUOM: values.EnerexCommercialUOM?.id ?? null,
          enerexLargeCommercialUOM: values.EnerexLargeCommercialUOM?.id ?? null,
        });
      }}
    >
      <div className="utility-sop-edit-layout">
        <div className="utility-sop-edit-column">
        <div className="utility-sop-field-group"><label htmlFor="utility-sop-edit-state">State</label>
        <Select inputId="utility-sop-edit-state" aria-label="State" isDisabled
          value={{ value: "state", label: stateLabel }} options={[]}
          getOptionValue={(option) => option.value} getOptionLabel={(option) => option.label} />
        </div>
        <Check
          id="utility-edit-name-key-required"
          name="KeyNameRequired"
          control={control}
          label="Name Key Required"
          disabled={saving || !getSopEditSnapshot(row)}
        />
        <FormInput type="text" name="KeyNameDescription" control={control} label="Name Key Description" disabled={saving || !getSopEditSnapshot(row) || !nameKeyRequired} />
        <FormInput type="number" name="EnrollmentLeadTime" control={control} label="Enrollment Lead Time days" min={0} step={1} disabled={saving || !getSopEditSnapshot(row)} />
        <FormInput type="select" name="EnerexCommercialUOM" control={control} label="Commercial UOM" options={uomOptions} isClearable isLoading={!uomOptions.length} disabled={saving || !getSopEditSnapshot(row)} />
        </div>
        <div className="utility-sop-edit-column">
        <fieldset className="utility-sop-edit-commodities" aria-label="Commodities">
          <div className="utility-sop-edit-commodity-options">
            <Check name="CommodityElectric" control={control} label={<CommodityIcon type="electric" />} disabled={saving || !getSopEditSnapshot(row)} />
            <Check name="CommodityGas" control={control} label={<CommodityIcon type="gas" />} disabled={saving || !getSopEditSnapshot(row)} />
          </div>
        </fieldset>
        <Check
          id="utility-edit-service-reference-required"
          name="ServiceReferenceNumberRequired"
          control={control}
          label="Service Reference Number Required"
          disabled={saving || !getSopEditSnapshot(row)}
        />
        <FormInput type="text" name="ServiceReferenceNumberDescription" control={control} label="Service Reference Number Description" disabled={saving || !getSopEditSnapshot(row) || !serviceReferenceRequired} />
        <FormInput type="select" name="EnerexResidentialUOM" control={control} label="Residential UOM" options={uomOptions} isClearable isLoading={!uomOptions.length} disabled={saving || !getSopEditSnapshot(row)} />
        <FormInput type="select" name="EnerexLargeCommercialUOM" control={control} label="Large commercial UOM" options={uomOptions} isClearable isLoading={!uomOptions.length} disabled={saving || !getSopEditSnapshot(row)} />
        </div>
      </div>
    </ConfirmationModal>
  );
};

export default EditStateOperationModal;
